import React, { useState } from 'react';
import { FlowNodeType, NodeCategory, NodeDefinition } from '../../types/flowBuilder';
import FlintSignLogo from '../../assets/fs_dark_logo.svg';
import {
  BoltIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  PlusCircleIcon,
  ClockIcon,
  ArrowRightCircleIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  BellIcon,
  ArrowsRightLeftIcon,
  CloudIcon,
  UserGroupIcon,
  EnvelopeIcon,
  CalendarIcon,
  DocumentPlusIcon,
  FunnelIcon,
  DocumentCheckIcon,
  CalculatorIcon,
  BugAntIcon,
  VariableIcon,
  EyeIcon,
  HashtagIcon,
  PencilSquareIcon,
  SparklesIcon,
  CodeBracketIcon,
  ListBulletIcon,
  CalendarDaysIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
  CpuChipIcon,
  PuzzlePieceIcon,
  BarsArrowUpIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  // AI Integration ikonok
  PhotoIcon,
  LanguageIcon,
  DocumentMagnifyingGlassIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  // Error Handling ikonok
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface FlowSidebarProps {
  onAddNode: (nodeType: FlowNodeType, position: { x: number; y: number }) => void;
}

// Node kategóriák és definíciók (n8n-szerű)
const nodeCategories: NodeCategory[] = [
  {
    id: 'triggers',
    name: 'Triggerek',
    icon: 'BoltIcon',
    color: 'text-yellow-600',
    nodes: [
      {
        type: 'webhook_trigger',
        name: 'Webhook',
        description: 'HTTP webhook trigger',
        icon: 'BoltIcon',
        color: 'bg-yellow-500',
        defaultConfig: { method: 'POST', path: '/webhook' },
        inputs: [],
        outputs: [{ name: 'data', type: 'object', required: true }],
        category: 'triggers'
      },
      {
        type: 'schedule_trigger',
        name: 'Ütemezett',
        description: 'Időzített indítás (cron)',
        icon: 'ClockIcon',
        color: 'bg-orange-500',
        defaultConfig: { cron: '0 9 * * 1-5', timezone: 'Europe/Budapest' },
        inputs: [],
        outputs: [{ name: 'timestamp', type: 'string', required: true }],
        category: 'triggers'
      },
      {
        type: 'form_trigger',
        name: 'Űrlap beküldés',
        description: 'Űrlap kitöltése alapú indítás',
        icon: 'DocumentTextIcon',
        color: 'bg-green-500',
        defaultConfig: { formId: '', validateRequired: true },
        inputs: [],
        outputs: [{ name: 'formData', type: 'object', required: true }],
        category: 'triggers'
      },
      {
        type: 'email_trigger',
        name: 'Email érkezés',
        description: 'Új email érkezésekor',
        icon: 'EnvelopeIcon',
        color: 'bg-blue-500',
        defaultConfig: { mailbox: 'inbox', filter: '' },
        inputs: [],
        outputs: [{ name: 'email', type: 'object', required: true }],
        category: 'triggers'
      },
      {
        type: 'file_trigger',
        name: 'Fájl feltöltés',
        description: 'Fájl feltöltésekor',
        icon: 'DocumentPlusIcon',
        color: 'bg-purple-500',
        defaultConfig: { folder: '/uploads', fileTypes: ['*'] },
        inputs: [],
        outputs: [{ name: 'file', type: 'object', required: true }],
        category: 'triggers'
      },
      {
        type: 'telegram_trigger',
        name: 'Telegram Üzenet',
        description: 'Bejövő Telegram üzenet',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-blue-500',
        defaultConfig: {
          botToken: '',
          allowedChats: [],
          commands: [],
          listenToAll: false
        },
        inputs: [],
        outputs: [
          { name: 'message', type: 'object', required: true },
          { name: 'chat', type: 'object', required: true },
          { name: 'user', type: 'object', required: true }
        ],
        category: 'triggers'
      },
      {
        type: 'database_trigger',
        name: 'Adatbázis változás',
        description: 'Adatbázis rekord változásakor',
        icon: 'CircleStackIcon',
        color: 'bg-indigo-500',
        defaultConfig: { table: '', operation: 'insert' },
        inputs: [],
        outputs: [{ name: 'record', type: 'object', required: true }],
        category: 'triggers'
      },
      {
        type: 'manual_trigger',
        name: 'Manuális trigger',
        description: 'Futtatáskor azonnal indul',
        icon: 'PlusCircleIcon',
        color: 'bg-gray-500',
        defaultConfig: { requireConfirmation: false },
        inputs: [],
        outputs: [{ name: 'user', type: 'object', required: true }],
        category: 'triggers'
      }
    ]
  },
  {
    id: 'actions',
    name: 'Műveletek',
    icon: 'CogIcon',
    color: 'text-blue-600',
    nodes: [
      {
        type: 'action',
        name: 'Általános Művelet',
        description: 'Egyedi művelet végrehajtása',
        icon: 'CogIcon',
        color: 'bg-blue-500',
        defaultConfig: {},
        inputs: [{ name: 'input', type: 'object', required: true }],
        outputs: [{ name: 'output', type: 'object', required: true }],
        category: 'actions'
      }
    ]
  },
  {
    id: 'logic',
    name: 'Logikai műveletek',
    icon: 'QuestionMarkCircleIcon',
    color: 'text-purple-600',
    nodes: [
      {
        type: 'condition',
        name: 'IF Feltétel',
        description: 'Feltételes elágazás',
        icon: 'QuestionMarkCircleIcon',
        color: 'bg-purple-500',
        defaultConfig: { condition: '' },
        inputs: [{ name: 'input', type: 'object', required: true }],
        outputs: [
          { name: 'true', type: 'object', required: false },
          { name: 'false', type: 'object', required: false }
        ],
        category: 'logic'
      },
      {
        type: 'switch',
        name: 'Switch',
        description: 'Többirányú elágazás',
        icon: 'ArrowPathIcon',
        color: 'bg-indigo-500',
        defaultConfig: { cases: [] },
        inputs: [{ name: 'input', type: 'object', required: true }],
        outputs: [{ name: 'output', type: 'object', required: false }],
        category: 'logic'
      },
      {
        type: 'merge',
        name: 'Merge',
        description: 'Ágak egyesítése',
        icon: 'ArrowsRightLeftIcon',
        color: 'bg-teal-500',
        defaultConfig: {},
        inputs: [
          { name: 'input1', type: 'object', required: false },
          { name: 'input2', type: 'object', required: false }
        ],
        outputs: [{ name: 'output', type: 'object', required: true }],
        category: 'logic'
      },
      {
        type: 'loop',
        name: 'Iteráció',
        description: 'Ismétlés megadott számban',
        icon: 'ArrowPathIcon',
        color: 'bg-amber-500',
        defaultConfig: { maxIterations: 10 },
        inputs: [{ name: 'data', type: 'array', required: true }],
        outputs: [
          { name: 'loop-body', type: 'object', required: true },
          { name: 'complete', type: 'object', required: true }
        ],
        category: 'logic'
      }
    ]
  },
  {
    id: 'timing',
    name: 'Időzítés',
    icon: 'ClockIcon',
    color: 'text-green-600',
    nodes: [
      {
        type: 'wait',
        name: 'Várakozás',
        description: 'Megadott ideig vár',
        icon: 'ClockIcon',
        color: 'bg-green-500',
        defaultConfig: { duration: 60, unit: 'seconds' },
        inputs: [{ name: 'input', type: 'object', required: true }],
        outputs: [{ name: 'output', type: 'object', required: true }],
        category: 'timing'
      }
    ]
  },
  {
    id: 'forms',
    name: 'Űrlapok',
    icon: 'DocumentTextIcon',
    color: 'text-orange-600',
    nodes: [
      {
        type: 'form',
        name: 'Űrlap',
        description: 'Dinamikus űrlap megjelenítés',
        icon: 'DocumentTextIcon',
        color: 'bg-orange-500',
        defaultConfig: { fields: [] },
        inputs: [{ name: 'prefill', type: 'object', required: false }],
        outputs: [{ name: 'formData', type: 'object', required: true }],
        category: 'forms'
      },
      {
        type: 'approval',
        name: 'Jóváhagyás',
        description: 'Jóváhagyási folyamat',
        icon: 'CheckCircleIcon',
        color: 'bg-emerald-500',
        defaultConfig: { approvers: [] },
        inputs: [{ name: 'request', type: 'object', required: true }],
        outputs: [
          { name: 'approved', type: 'object', required: false },
          { name: 'rejected', type: 'object', required: false }
        ],
        category: 'forms'
      }
    ]
  },
  {
    id: 'notifications',
    name: 'Értesítések',
    icon: 'BellIcon',
    color: 'text-red-600',
    nodes: [
      {
        type: 'notification',
        name: 'Email',
        description: 'Email küldés',
        icon: 'EnvelopeIcon',
        color: 'bg-red-500',
        defaultConfig: { template: '' },
        inputs: [
          { name: 'recipient', type: 'string', required: true },
          { name: 'subject', type: 'string', required: true },
          { name: 'body', type: 'string', required: true }
        ],
        outputs: [{ name: 'result', type: 'object', required: true }],
        category: 'notifications'
      },
      {
        type: 'smtp_email',
        name: 'SMTP Email',
        description: 'Email küldés egyedi SMTP-vel',
        icon: 'ServerIcon',
        color: 'bg-orange-500',
        defaultConfig: { 
          smtp: {
            host: 'smtp.gmail.com',
            port: 587,
            secure: false,
            user: '',
            pass: ''
          },
          from: {
            name: 'YouCRM',
            email: ''
          }
        },
        inputs: [
          { name: 'recipient', type: 'string', required: true },
          { name: 'subject', type: 'string', required: true },
          { name: 'body', type: 'string', required: true },
          { name: 'smtp_config', type: 'object', required: false }
        ],
        outputs: [{ name: 'result', type: 'object', required: true }],
        category: 'notifications'
      },
      {
        type: 'telegram_message',
        name: 'Telegram Üzenet',
        description: 'Telegram üzenet küldés',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-blue-500',
        defaultConfig: {
          botToken: '',
          chatId: '',
          parseMode: 'HTML'
        },
        inputs: [
          { name: 'message', type: 'string', required: true },
          { name: 'chatId', type: 'string', required: false },
          { name: 'replyToMessageId', type: 'number', required: false }
        ],
        outputs: [{ name: 'result', type: 'object', required: true }],
        category: 'notifications'
      },
      {
        type: 'sms_send',
        name: 'SMS Küldés',
        description: 'SMS üzenet küldés Twilio-val',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-green-500',
        defaultConfig: {
          accountSid: '',
          authToken: '',
          fromNumber: ''
        },
        inputs: [
          { name: 'to', type: 'string', required: true },
          { name: 'message', type: 'string', required: true }
        ],
        outputs: [{ name: 'result', type: 'object', required: true }],
        category: 'notifications'
      }
    ]
  },
  {
    id: 'crm',
    name: 'CRM Műveletek',
    icon: 'UserGroupIcon',
    color: 'text-cyan-600',
    nodes: [
      {
        type: 'crm_action',
        name: 'Ügyfél létrehozás',
        description: 'Új ügyfél létrehozása',
        icon: 'UserGroupIcon',
        color: 'bg-cyan-500',
        defaultConfig: { type: 'create_customer' },
        inputs: [{ name: 'customerData', type: 'customer', required: true }],
        outputs: [{ name: 'customer', type: 'customer', required: true }],
        category: 'crm'
      },
      {
        type: 'crm_action',
        name: 'Projekt létrehozás',
        description: 'Új projekt indítása',
        icon: 'DocumentPlusIcon',
        color: 'bg-blue-600',
        defaultConfig: { type: 'create_project' },
        inputs: [{ name: 'projectData', type: 'project', required: true }],
        outputs: [{ name: 'project', type: 'project', required: true }],
        category: 'crm'
      },
      {
        type: 'crm_action',
        name: 'Naptár esemény',
        description: 'Naptár esemény létrehozása',
        icon: 'CalendarIcon',
        color: 'bg-purple-600',
        defaultConfig: { type: 'create_calendar_event' },
        inputs: [{ name: 'eventData', type: 'object', required: true }],
        outputs: [{ name: 'event', type: 'object', required: true }],
        category: 'crm'
      }
    ]
  },
  {
    id: 'integrations',
    name: 'Integrációk',
    icon: 'CloudIcon',
    color: 'text-gray-600',
    nodes: [
      {
        type: 'integration',
        name: 'HTTP Request',
        description: 'HTTP API hívás',
        icon: 'CloudIcon',
        color: 'bg-gray-500',
        defaultConfig: { method: 'GET', url: '' },
        inputs: [{ name: 'parameters', type: 'object', required: false }],
        outputs: [{ name: 'response', type: 'object', required: true }],
        category: 'integrations'
      },
          {
            type: 'flintsign_sign',
            name: 'FlintSign Aláírás',
            description: 'Digitális aláírás FlintSign-nal',
            icon: 'FlintSignLogo',
            color: 'bg-emerald-500',
        defaultConfig: {
          apiKey: '',
          certificateId: '',
          inputFile: '',
          signaturePosition: 'bottom-right',
          signatureReason: 'Digitális aláírás'
        },
        inputs: [
          { name: 'file', type: 'file', required: true },
          { name: 'certificateId', type: 'string', required: false }
        ],
        outputs: [
          { name: 'signedFile', type: 'file', required: true },
          { name: 'signatureId', type: 'string', required: true },
          { name: 'result', type: 'object', required: true }
        ],
        category: 'integrations'
      }
    ]
  },
  {
    id: 'file_operations',
    name: 'Fájl Műveletek',
    icon: 'DocumentTextIcon',
    color: 'text-rose-600',
    nodes: [
      {
        type: 'file_read',
        name: 'Fájl olvasás',
        description: 'Fájl tartalmának beolvasása',
        icon: 'DocumentTextIcon',
        color: 'bg-rose-500',
        defaultConfig: { encoding: 'utf-8' },
        inputs: [{ name: 'file_path', type: 'string', required: true }],
        outputs: [{ name: 'content', type: 'string', required: true }],
        category: 'file_operations'
      },
      {
        type: 'file_write',
        name: 'Fájl írás',
        description: 'Tartalom fájlba írása',
        icon: 'DocumentPlusIcon',
        color: 'bg-rose-600',
        defaultConfig: { encoding: 'utf-8', overwrite: false },
        inputs: [
          { name: 'file_path', type: 'string', required: true },
          { name: 'content', type: 'string', required: true }
        ],
        outputs: [{ name: 'success', type: 'boolean', required: true }],
        category: 'file_operations'
      },
      {
        type: 'pdf_generator',
        name: 'PDF Generálás',
        description: 'PDF fájl létrehozása',
        icon: 'DocumentTextIcon',
        color: 'bg-red-500',
        defaultConfig: { template: '', format: 'A4' },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'pdf_url', type: 'string', required: true }],
        category: 'file_operations'
      },
      {
        type: 'excel_processor',
        name: 'Excel feldolgozó',
        description: 'Excel fájlok kezelése',
        icon: 'DocumentCheckIcon',
        color: 'bg-green-600',
        defaultConfig: { operation: 'read', sheet: 'Sheet1' },
        inputs: [{ name: 'file', type: 'file', required: true }],
        outputs: [{ name: 'data', type: 'array', required: true }],
        category: 'file_operations'
      },
      {
        type: 'image_processor',
        name: 'Kép feldolgozó',
        description: 'Képek átméretezése, konvertálása',
        icon: 'PhotoIcon',
        color: 'bg-purple-500',
        defaultConfig: { operation: 'resize', width: 800, height: 600 },
        inputs: [{ name: 'image', type: 'file', required: true }],
        outputs: [{ name: 'processed_image', type: 'file', required: true }],
        category: 'file_operations'
      },
      {
        type: 'file_converter',
        name: 'Fájl konvertáló',
        description: 'Fájlok konvertálása különböző formátumokba',
        icon: 'ArrowsRightLeftIcon',
        color: 'bg-pink-500',
        defaultConfig: { from: 'docx', to: 'pdf' },
        inputs: [{ name: 'file', type: 'file', required: true }],
        outputs: [{ name: 'converted_file', type: 'file', required: true }],
        category: 'file_operations'
      }
    ]
  },
  {
    id: 'data_processing',
    name: 'Adatfeldolgozás',
    icon: 'CircleStackIcon',
    color: 'text-indigo-600',
    nodes: [
      {
        type: 'data_transform',
        name: 'Adatátalakítás',
        description: 'Adatok formázása és átalakítása',
        icon: 'ArrowsRightLeftIcon',
        color: 'bg-indigo-500',
        defaultConfig: { transformType: 'map' },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'transformed', type: 'object', required: true }],
        category: 'data_processing'
      },
      {
        type: 'data_filter',
        name: 'Adatok szűrése',
        description: 'Adatok szűrése feltételek alapján',
        icon: 'FunnelIcon',
        color: 'bg-indigo-600',
        defaultConfig: { filterType: 'equals' },
        inputs: [{ name: 'data', type: 'array', required: true }],
        outputs: [{ name: 'filtered', type: 'array', required: true }],
        category: 'data_processing'
      },
      {
        type: 'data_sort',
        name: 'Adatok rendezése',
        description: 'Lista elemeinek rendezése',
        icon: 'BarsArrowUpIcon',
        color: 'bg-indigo-700',
        defaultConfig: { sortBy: 'name', order: 'asc' },
        inputs: [{ name: 'data', type: 'array', required: true }],
        outputs: [{ name: 'sorted', type: 'array', required: true }],
        category: 'data_processing'
      },
      {
        type: 'json_processor',
        name: 'JSON feldolgozó',
        description: 'JSON adatok kezelése',
        icon: 'CodeBracketIcon',
        color: 'bg-slate-500',
        defaultConfig: { operation: 'parse' },
        inputs: [{ name: 'input', type: 'string', required: true }],
        outputs: [{ name: 'output', type: 'object', required: true }],
        category: 'data_processing'
      }
    ]
  },
  {
    id: 'utilities',
    name: 'Segédeszközök',
    icon: 'WrenchScrewdriverIcon',
    color: 'text-emerald-600',
    nodes: [
      {
        type: 'debug_logger',
        name: 'Debug napló',
        description: 'Adatok naplózása hibakereséshez',
        icon: 'BugAntIcon',
        color: 'bg-emerald-500',
        defaultConfig: { logLevel: 'info' },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'data', type: 'object', required: true }],
        category: 'utilities'
      },
      {
        type: 'variable_set',
        name: 'Változó beállítás',
        description: 'Változó értékének beállítása',
        icon: 'VariableIcon',
        color: 'bg-emerald-600',
        defaultConfig: { variableName: 'myVariable' },
        inputs: [{ name: 'value', type: 'any', required: true }],
        outputs: [{ name: 'value', type: 'any', required: true }],
        category: 'utilities'
      },
      {
        type: 'variable_get',
        name: 'Változó lekérdezés',
        description: 'Változó értékének lekérdezése',
        icon: 'EyeIcon',
        color: 'bg-emerald-700',
        defaultConfig: { variableName: 'myVariable' },
        inputs: [],
        outputs: [{ name: 'value', type: 'any', required: true }],
        category: 'utilities'
      },
      {
        type: 'counter',
        name: 'Számláló',
        description: 'Számláló érték kezelése',
        icon: 'HashtagIcon',
        color: 'bg-teal-500',
        defaultConfig: { initialValue: 0, increment: 1 },
        inputs: [{ name: 'trigger', type: 'object', required: false }],
        outputs: [{ name: 'count', type: 'number', required: true }],
        category: 'utilities'
      }
    ]
  },
  {
    id: 'text_math',
    name: 'Szöveg és számítás',
    icon: 'CalculatorIcon',
    color: 'text-amber-600',
    nodes: [
      {
        type: 'text_processor',
        name: 'Szöveg feldolgozó',
        description: 'Szöveg műveletek',
        icon: 'PencilSquareIcon',
        color: 'bg-amber-500',
        defaultConfig: { operation: 'uppercase' },
        inputs: [{ name: 'text', type: 'string', required: true }],
        outputs: [{ name: 'result', type: 'string', required: true }],
        category: 'text_math'
      },
      {
        type: 'string_formatter',
        name: 'Szöveg formázó',
        description: 'Szöveg formázása sablonnal',
        icon: 'DocumentTextIcon',
        color: 'bg-amber-600',
        defaultConfig: { template: 'Hello {{name}}!' },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'formatted', type: 'string', required: true }],
        category: 'text_math'
      },
      {
        type: 'math_operation',
        name: 'Matematikai művelet',
        description: 'Számítások végrehajtása',
        icon: 'CalculatorIcon',
        color: 'bg-orange-500',
        defaultConfig: { operation: 'add' },
        inputs: [
          { name: 'a', type: 'number', required: true },
          { name: 'b', type: 'number', required: true }
        ],
        outputs: [{ name: 'result', type: 'number', required: true }],
        category: 'text_math'
      },
      {
        type: 'random_generator',
        name: 'Véletlen generátor',
        description: 'Véletlen értékek generálása',
        icon: 'SparklesIcon',
        color: 'bg-pink-500',
        defaultConfig: { type: 'number', min: 1, max: 100 },
        inputs: [],
        outputs: [{ name: 'value', type: 'any', required: true }],
        category: 'text_math'
      }
    ]
  },
  {
    id: 'advanced_logic',
    name: 'Haladó logika',
    icon: 'CpuChipIcon',
    color: 'text-violet-600',
    nodes: [
      {
        type: 'multi_condition',
        name: 'Összetett feltétel',
        description: 'Több feltétel kombinálása (AND/OR)',
        icon: 'PuzzlePieceIcon',
        color: 'bg-violet-500',
        defaultConfig: { conditions: [], operator: 'AND' },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [
          { name: 'true', type: 'object', required: false },
          { name: 'false', type: 'object', required: false }
        ],
        category: 'advanced_logic'
      },
      {
        type: 'list_processor',
        name: 'Lista feldolgozó',
        description: 'Lista műveletek (map, reduce, filter)',
        icon: 'ListBulletIcon',
        color: 'bg-violet-600',
        defaultConfig: { operation: 'map' },
        inputs: [{ name: 'list', type: 'array', required: true }],
        outputs: [{ name: 'result', type: 'array', required: true }],
        category: 'advanced_logic'
      },
      {
        type: 'date_time',
        name: 'Dátum/idő',
        description: 'Dátum és idő műveletek',
        icon: 'CalendarDaysIcon',
        color: 'bg-sky-500',
        defaultConfig: { operation: 'now' },
        inputs: [{ name: 'date', type: 'string', required: false }],
        outputs: [{ name: 'result', type: 'string', required: true }],
        category: 'advanced_logic'
      }
    ]
  },
  {
    id: 'ai_integration',
    name: 'AI Integráció',
    icon: 'CpuChipIcon',
    color: 'text-pink-600',
    nodes: [
      {
        type: 'openai_chat',
        name: 'OpenAI ChatGPT',
        description: 'ChatGPT AI beszélgetés és szöveg generálás',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-green-500',
        defaultConfig: { 
          apiKey: '', 
          model: 'gpt-3.5-turbo', 
          prompt: '', 
          maxTokens: 1000, 
          temperature: 0.7 
        },
        inputs: [{ name: 'prompt', type: 'string', required: true }],
        outputs: [
          { name: 'response', type: 'string', required: true },
          { name: 'tokens_used', type: 'number', required: false }
        ],
        category: 'ai_integration'
      },
      {
        type: 'claude_chat',
        name: 'Anthropic Claude',
        description: 'Claude AI asszisztens integráció',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-orange-500',
        defaultConfig: { 
          apiKey: '', 
          model: 'claude-3-sonnet-20240229', 
          prompt: '', 
          maxTokens: 1000 
        },
        inputs: [{ name: 'prompt', type: 'string', required: true }],
        outputs: [{ name: 'response', type: 'string', required: true }],
        category: 'ai_integration'
      },
      {
        type: 'deepseek_chat',
        name: 'DeepSeek AI',
        description: 'DeepSeek AI modell integráció',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-blue-500',
        defaultConfig: { 
          apiKey: '', 
          model: 'deepseek-chat', 
          prompt: '' 
        },
        inputs: [{ name: 'prompt', type: 'string', required: true }],
        outputs: [{ name: 'response', type: 'string', required: true }],
        category: 'ai_integration'
      },
      {
        type: 'gemini_chat',
        name: 'Google Gemini',
        description: 'Gemini AI modell integráció',
        icon: 'ChatBubbleLeftRightIcon',
        color: 'bg-purple-500',
        defaultConfig: { 
          apiKey: '', 
          model: 'gemini-pro', 
          prompt: '' 
        },
        inputs: [{ name: 'prompt', type: 'string', required: true }],
        outputs: [{ name: 'response', type: 'string', required: true }],
        category: 'ai_integration'
      },
      {
        type: 'ai_text_generator',
        name: 'AI Szöveg Generátor',
        description: 'Automatikus szöveg generálás',
        icon: 'PencilIcon',
        color: 'bg-indigo-500',
        defaultConfig: { 
          provider: 'openai', 
          textType: 'article', 
          topic: '', 
          length: 'medium' 
        },
        inputs: [{ name: 'topic', type: 'string', required: true }],
        outputs: [
          { name: 'generated_text', type: 'string', required: true },
          { name: 'word_count', type: 'number', required: false }
        ],
        category: 'ai_integration'
      },
      {
        type: 'ai_image_generator',
        name: 'AI Kép Generátor',
        description: 'AI képgenerálás (DALL-E, Midjourney)',
        icon: 'PhotoIcon',
        color: 'bg-pink-500',
        defaultConfig: { 
          provider: 'dalle', 
          prompt: '', 
          size: '1024x1024', 
          style: 'natural' 
        },
        inputs: [{ name: 'prompt', type: 'string', required: true }],
        outputs: [{ name: 'image_url', type: 'string', required: true }],
        category: 'ai_integration'
      },
      {
        type: 'ai_text_analyzer',
        name: 'AI Szöveg Elemző',
        description: 'Szöveg elemzés és kategorizálás',
        icon: 'DocumentMagnifyingGlassIcon',
        color: 'bg-cyan-500',
        defaultConfig: { 
          text: '', 
          analysisType: 'sentiment' 
        },
        inputs: [{ name: 'text', type: 'string', required: true }],
        outputs: [{ name: 'analysis', type: 'object', required: true }],
        category: 'ai_integration'
      },
      {
        type: 'ai_translator',
        name: 'AI Fordító',
        description: 'Automatikus nyelvfordítás',
        icon: 'LanguageIcon',
        color: 'bg-emerald-500',
        defaultConfig: { 
          text: '', 
          sourceLang: 'auto', 
          targetLang: 'en' 
        },
        inputs: [{ name: 'text', type: 'string', required: true }],
        outputs: [
          { name: 'translated_text', type: 'string', required: true },
          { name: 'source_language', type: 'string', required: false }
        ],
        category: 'ai_integration'
      },
      {
        type: 'ai_summarizer',
        name: 'AI Összefoglaló',
        description: 'Szöveg összefoglalás',
        icon: 'DocumentDuplicateIcon',
        color: 'bg-amber-500',
        defaultConfig: { 
          text: '', 
          summaryLength: 'medium' 
        },
        inputs: [{ name: 'text', type: 'string', required: true }],
        outputs: [
          { name: 'summary', type: 'string', required: true },
          { name: 'compression_ratio', type: 'number', required: false }
        ],
        category: 'ai_integration'
      }
    ]
  },
  {
    id: 'data_parsers',
    name: 'Adat Parserek',
    icon: 'CodeBracketIcon',
    color: 'text-teal-600',
    nodes: [
      {
        type: 'csv_parser',
        name: 'CSV Feldolgozó',
        description: 'CSV ↔ JSON konverzió',
        icon: 'DocumentTextIcon',
        color: 'bg-teal-500',
        defaultConfig: { operation: 'parse', delimiter: ',', hasHeader: true },
        inputs: [{ name: 'input', type: 'string', required: true }],
        outputs: [{ name: 'data', type: 'array', required: true }],
        category: 'data_parsers'
      },
      {
        type: 'xml_parser',
        name: 'XML Feldolgozó',
        description: 'XML → JSON konverzió',
        icon: 'CodeBracketIcon',
        color: 'bg-orange-500',
        defaultConfig: { preserveAttributes: true },
        inputs: [{ name: 'xml', type: 'string', required: true }],
        outputs: [{ name: 'json', type: 'object', required: true }],
        category: 'data_parsers'
      },
      {
        type: 'yaml_parser',
        name: 'YAML Feldolgozó',
        description: 'YAML ↔ JSON konverzió',
        icon: 'DocumentDuplicateIcon',
        color: 'bg-cyan-500',
        defaultConfig: { operation: 'parse' },
        inputs: [{ name: 'input', type: 'string', required: true }],
        outputs: [{ name: 'json', type: 'object', required: true }],
        category: 'data_parsers'
      },
      {
        type: 'base64_coder',
        name: 'Base64 Kódoló',
        description: 'Base64 encode/decode',
        icon: 'KeyIcon',
        color: 'bg-lime-500',
        defaultConfig: { operation: 'encode' },
        inputs: [{ name: 'input', type: 'string', required: true }],
        outputs: [{ name: 'result', type: 'string', required: true }],
        category: 'data_parsers'
      },
      {
        type: 'data_validator',
        name: 'Adat Validátor',
        description: 'Adat validálás szabályokkal',
        icon: 'CheckCircleIcon',
        color: 'bg-emerald-500',
        defaultConfig: { rules: [], stopOnFirstError: false },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [
          { name: 'valid', type: 'object', required: false },
          { name: 'invalid', type: 'object', required: false }
        ],
        category: 'data_parsers'
      }
    ]
  },
  {
    id: 'database',
    name: 'Adatbázis',
    icon: 'CircleStackIcon',
    color: 'text-indigo-600',
    nodes: [
      {
        type: 'database_query',
        name: 'Database Query',
        description: 'SQL SELECT lekérdezés',
        icon: 'CircleStackIcon',
        color: 'bg-indigo-500',
        defaultConfig: { operation: 'select', table: '', query: '' },
        inputs: [{ name: 'parameters', type: 'object', required: false }],
        outputs: [{ name: 'rows', type: 'array', required: true }],
        category: 'database'
      },
      {
        type: 'database_insert',
        name: 'Database Insert',
        description: 'Új rekord beszúrása',
        icon: 'PlusCircleIcon',
        color: 'bg-indigo-600',
        defaultConfig: { table: '', data: {} },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'id', type: 'string', required: true }],
        category: 'database'
      },
      {
        type: 'database_update',
        name: 'Database Update',
        description: 'Rekord frissítése',
        icon: 'ArrowPathIcon',
        color: 'bg-indigo-600',
        defaultConfig: { table: '', data: {}, where: {} },
        inputs: [{ name: 'data', type: 'object', required: true }],
        outputs: [{ name: 'affected_rows', type: 'number', required: true }],
        category: 'database'
      },
      {
        type: 'database_delete',
        name: 'Database Delete',
        description: 'Rekord törlése',
        icon: 'TrashIcon',
        color: 'bg-indigo-700',
        defaultConfig: { table: '', where: {} },
        inputs: [{ name: 'where', type: 'object', required: true }],
        outputs: [{ name: 'affected_rows', type: 'number', required: true }],
        category: 'database'
      }
    ]
  },
  {
    id: 'error_handling',
    name: 'Hibakezelés',
    icon: 'ShieldCheckIcon',
    color: 'text-amber-600',
    nodes: [
      {
        type: 'try_catch',
        name: 'Try-Catch',
        description: 'Hibakezelés blokk',
        icon: 'ShieldCheckIcon',
        color: 'bg-amber-500',
        defaultConfig: { catchEnabled: true, continueOnError: false },
        inputs: [{ name: 'input', type: 'any', required: true }],
        outputs: [
          { name: 'success', type: 'object', required: false },
          { name: 'error', type: 'object', required: false }
        ],
        category: 'error_handling'
      },
      {
        type: 'retry',
        name: 'Retry',
        description: 'Újrapróbálkozás N-szer',
        icon: 'ArrowPathIcon',
        color: 'bg-blue-500',
        defaultConfig: { maxRetries: 3, strategy: 'fixed', delaySeconds: 1 },
        inputs: [{ name: 'action', type: 'any', required: true }],
        outputs: [
          { name: 'success', type: 'object', required: false },
          { name: 'failed', type: 'object', required: false }
        ],
        category: 'error_handling'
      },
      {
        type: 'error_logger',
        name: 'Error Logger',
        description: 'Hiba naplózás',
        icon: 'ExclamationTriangleIcon',
        color: 'bg-red-500',
        defaultConfig: { logLevel: 'error', destination: 'console' },
        inputs: [{ name: 'error', type: 'object', required: true }],
        outputs: [{ name: 'logged', type: 'boolean', required: true }],
        category: 'error_handling'
      },
      {
        type: 'timeout',
        name: 'Timeout',
        description: 'Időtúllépés kezelés',
        icon: 'ClockIcon',
        color: 'bg-orange-500',
        defaultConfig: { timeoutSeconds: 30, throwOnTimeout: false },
        inputs: [{ name: 'action', type: 'any', required: true }],
        outputs: [
          { name: 'completed', type: 'object', required: false },
          { name: 'timeout', type: 'object', required: false }
        ],
        category: 'error_handling'
      }
    ]
  }
];

const FlowSidebar: React.FC<FlowSidebarProps> = ({ onAddNode }) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['triggers', 'actions', 'ai_integration']);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Node drag kezdete
  const onDragStart = (event: React.DragEvent, nodeType: FlowNodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Mobil node hozzáadás (kattintásra)
  const onMobileNodeAdd = (nodeType: FlowNodeType) => {
    // Canvas közepére helyezzük a node-ot kis véletlenszerű eltéréssel
    const position = { 
      x: 200 + Math.random() * 100, 
      y: 150 + Math.random() * 100 
    };
    onAddNode(nodeType, position);
  };

  // Szűrt node-ok
  const filteredCategories = nodeCategories.map(category => ({
    ...category,
    nodes: category.nodes.filter(node =>
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.nodes.length > 0);

  // Icon komponens lekérése név alapján
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      BoltIcon,
      CogIcon,
      QuestionMarkCircleIcon,
      ArrowPathIcon,
      ClockIcon,
      DocumentTextIcon,
      CheckCircleIcon,
      BellIcon,
      ArrowsRightLeftIcon,
      CloudIcon,
      UserGroupIcon,
      EnvelopeIcon,
      CalendarIcon,
      DocumentPlusIcon,
      FunnelIcon,
      CalculatorIcon,
      BugAntIcon,
      VariableIcon,
      EyeIcon,
      HashtagIcon,
      PencilSquareIcon,
      SparklesIcon,
      CodeBracketIcon,
      ListBulletIcon,
      CalendarDaysIcon,
      CircleStackIcon,
      WrenchScrewdriverIcon,
      CpuChipIcon,
      PuzzlePieceIcon,
      BarsArrowUpIcon,
    };
    
    // Custom logók kezelése
    if (iconName === 'FlintSignLogo') {
      return () => (
        <div className="w-5 h-5 bg-white rounded flex items-center justify-center p-0.5">
          <img 
            src={FlintSignLogo} 
            alt="FlintSign" 
            className="w-full h-full object-contain"
          />
        </div>
      );
    }
    
    return iconMap[iconName] || CogIcon;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
          Node Paletta
        </h3>
        
        {/* Keresés */}
        <input
          type="text"
          placeholder="Node keresése..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Node kategóriák */}
      <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {filteredCategories.map((category) => {
          const isExpanded = expandedCategories.includes(category.id);
          const CategoryIcon = getIconComponent(category.icon);

          return (
            <div key={category.id} className="space-y-2">
              {/* Kategória header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-2 text-left bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {category.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({category.nodes.length})
                  </span>
                </div>
                <ArrowRightCircleIcon
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {/* Node lista */}
              {isExpanded && (
                <div className="space-y-1 ml-2">
                  {category.nodes.map((node) => {
                    const NodeIcon = getIconComponent(node.icon);
                    
                    return (
                      <div
                        key={`${node.type}-${node.name}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, node.type)}
                        onClick={() => onMobileNodeAdd(node.type)}
                        className="flex items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg cursor-move hover:shadow-md transition-shadow group md:cursor-move cursor-pointer"
                      >
                        <div className={`w-8 h-8 ${node.color} rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform`}>
                          <NodeIcon className="w-4 h-4 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {node.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {node.description}
                          </p>
                        </div>
                        
                        <PlusCircleIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer info */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Húzza a node-okat a canvas-ra az építéshez
        </p>
      </div>
    </div>
  );
};

export default FlowSidebar;